import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Search, ShieldCheck, ShieldOff, Loader2 } from "lucide-react";
import CommonGrid from "@/components/pos/admin/common/grid";
import NoRecordFound from "@/components/pos/admin/common/no-record-found";
import ActionTemplate from "@/components/pos/admin/common/action-template";
import ConfirmModal from "@/components/pos/admin/common/confirmation-modal";
import { usePagePersistentState } from "@/hooks/pos/use-page-persisted-state";
import { useDebounce } from "@/hooks/pos/use-debounce";
import { useSortableTable } from "@/hooks/pos/use-sortable-table";
import { useDateFormatter } from "@/hooks/pos/use-date-formatter";
import { posApiFetch } from "@/utils/pos-api";
import { useToast } from "@/hooks/use-toast";
import { UserRole } from "@/constants";
import { FEATURES } from "@/lib/config";
import useSWR from "swr";

interface AdminUser {
  id: string;
  email: string;
  displayName: string | null;
  walletAddress: string | null;
  role: UserRole;
  createdAt: string;
  lastLoginAt: string | null;
  emailVerified: boolean;
}

interface AdminUsersResponse {
  data: AdminUser[];
  meta: {
    currentPage: number;
    pageSize: number;
    total: number;
  };
}

type UsersState = {
  searchQuery?: string;
  currentPage?: number;
  itemsPerPage?: number;
};

const defaultUsersState: UsersState = {
  searchQuery: "",
  currentPage: 1,
  itemsPerPage: 10,
};

const formatWalletAddress = (address: string | null) => {
  if (!address) return "Not connected";
  return `${address.slice(0, 6)}...${address.slice(-4)}`;
};

export default function UsersPage() {
  const { toast } = useToast();
  const { formatDate } = useDateFormatter();

  useEffect(() => {
    document.title = "Users | SECH Admin";
  }, []);

  const [pageState, setPageState] = usePagePersistentState<UsersState>("users", defaultUsersState);
  const searchQuery = pageState.searchQuery;
  const currentPage = pageState.currentPage;
  const itemsPerPage = pageState.itemsPerPage;
  const debouncedSearch = useDebounce(searchQuery?.trim() ?? "", 300);

  // Sorting is resolved server-side now that the list is paginated — the hook
  // only holds the sort state (column + direction) and feeds it into the
  // request as sortBy/sortDir. Only columns the API whitelists are sortable.
  const { sortColumn, sortDirection, handleSort } = useSortableTable<string>({
    storageKey: "users_table",
    defaultColumn: "createdAt",
    defaultDirection: "desc",
  });

  // Changing the sort can shift which rows fall on the current page, so reset
  // back to page 1 whenever the user re-sorts.
  const handleSortChange = (column: string) => {
    handleSort(column);
    setPageState((prev) => ({ ...prev, currentPage: 1 }));
  };

  const [verifyingUser, setVerifyingUser] = useState<AdminUser | null>(null);
  const [verifyingUserId, setVerifyingUserId] = useState<string | null>(null);

  const [mfaResetUser, setMfaResetUser] = useState<AdminUser | null>(null);
  const [mfaResetUserId, setMfaResetUserId] = useState<string | null>(null);

  // Server-side pagination/search: fetch only the page being viewed instead of
  // pulling every user at once. The list endpoint resolves Firebase auth data
  // (lastLoginAt / emailVerified) per row, so loading all users was slow — this
  // keeps each request to a single page worth of lookups.
  const { data: usersResponse, error, isLoading, mutate } = useSWR<AdminUsersResponse>(
    ["/v1/pos/admin/users", currentPage, itemsPerPage, debouncedSearch, sortColumn, sortDirection],
    async ([, page, pageSize, search, sortBy, sortDir]: [string, number | undefined, number | undefined, string, string, string]) => {
      const params = new URLSearchParams();
      params.append("page", String(page ?? 1));
      params.append("pageSize", String(pageSize ?? 10));
      if (search) params.append("search", search);
      if (sortBy) params.append("sortBy", sortBy);
      if (sortDir) params.append("sortDir", sortDir);
      const response = await posApiFetch(`/v1/pos/admin/users?${params.toString()}`);
      return response.json();
    },
    { keepPreviousData: true }
  );

  const usersData = usersResponse?.data ?? [];

  const handleVerifyUser = async () => {
    if (!verifyingUser) return;
    const user = verifyingUser;
    setVerifyingUserId(user.id);
    setVerifyingUser(null);
    try {
      const response = await posApiFetch(`/v1/pos/admin/users/${user.id}/verify`, {
        method: "PATCH",
      });
      if (!response.ok) throw new Error("Failed to verify user");
      toast({ title: "User verified", description: `${user.email} has been manually verified.` });
      mutate();
    } catch (error) {
      toast({
        title: "Failed to verify user",
        description: error instanceof Error ? error.message : "Unknown error",
        variant: "destructive",
      });
    } finally {
      setVerifyingUserId(null);
    }
  };

  const handleResetMfa = async () => {
    if (!mfaResetUser) return;
    const user = mfaResetUser;
    setMfaResetUserId(user.id);
    setMfaResetUser(null);
    try {
      const response = await posApiFetch(`/v1/pos/admin/users/${user.id}/mfa`, {
        method: "DELETE",
      });
      const result = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(result?.error?.message || "Failed to reset 2FA");
      }
      const removedCount = Array.isArray(result?.data?.removedFactors)
        ? result.data.removedFactors.length
        : 0;
      if (result?.data?.unenrolled && removedCount > 0) {
        toast({
          title: "2FA reset",
          description: `Removed ${removedCount} factor${removedCount === 1 ? "" : "s"} for ${user.email}. They can sign in with email and password.`,
        });
      } else {
        toast({
          title: "Nothing to reset",
          description: `${user.email} did not have 2FA enrolled.`,
        });
      }
      mutate();
    } catch (error) {
      toast({
        title: "Failed to reset 2FA",
        description: error instanceof Error ? error.message : "Unknown error",
        variant: "destructive",
      });
    } finally {
      setMfaResetUserId(null);
    }
  };

  const tableColumns = useMemo(() => [
    {
      label: "Email", field: "email", sortable: true,
      cellTemplate: (value: string) => (
        <span className="text-[13px] text-[var(--secondary-text-color)] font-medium">{value}</span>
      ),
    },
    {
      label: "Name", field: "displayName", sortable: true,
      cellTemplate: (value: string | null) => (
        <span className="text-[13px] text-[var(--secondary-text-color)]">{value || "N/A"}</span>
      ),
    },
    {
      label: "Wallet", field: "walletAddress",
      cellTemplate: (value: string | null) => (
        <span className="text-[13px] text-[var(--secondary-text-color)] font-mono">{formatWalletAddress(value)}</span>
      ),
    },
    {
      label: "Role", field: "role", sortable: true,
      cellTemplate: (value: string) => (
        <Badge variant={value === UserRole.ADMIN ? "default" : "secondary"} className="text-[11px]">
          {value}
        </Badge>
      ),
    },
    {
      label: "Joined", field: "createdAt", sortable: true,
      cellTemplate: (value: string) => formatDate(value),
    },
    {
      label: "Last Login", field: "lastLoginAt",
      cellTemplate: (value: string | null) => value ? formatDate(value) : "N/A",
    },
    {
      label: "Verified", field: "emailVerified",
      cellTemplate: (value: boolean) => (
        <span className={`px-2 h-5 flex items-center w-fit rounded-full text-xs font-medium ${value ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-600"}`}>
          <span className={`h-2 w-2 rounded-full mr-1 ${value ? "bg-green-800" : "bg-gray-500"} inline-flex items-center justify-center`} />
          <span className="leading-5 text-[13px] font-normal">{value ? "Yes" : "No"}</span>
        </span>
      ),
    },
    {
      label: "Action", field: "id",
      headerTemplate: () => <span className="text-[13px]"><span className="h-[28px] w-[28px] flex items-center justify-center">Action</span></span>,
      cellTemplate: (_value: string, row: AdminUser) => {
        if (verifyingUserId === row.id || mfaResetUserId === row.id) {
          return <Loader2 className="h-4 w-4 animate-spin text-[var(--secondary-text-color)]" />;
        }
        return (
          <ActionTemplate
            row={row}
            actions={[
              {
                label: "Verify Email",
                icon: ShieldCheck,
                onClick: (r: AdminUser) => setVerifyingUser(r),
              },
              // Reset 2FA is hidden while MFA is disabled at the Firebase level —
              // no user can enroll, so there's nothing to reset.
              ...(FEATURES.MFA_ENABLED
                ? [{
                    label: "Reset 2FA",
                    icon: ShieldOff,
                    onClick: (r: AdminUser) => setMfaResetUser(r),
                  }]
                : []),
            ]}
          />
        );
      },
    },
  ], [verifyingUserId, mfaResetUserId]);

  // Search/sort/pagination are all resolved server-side; `usersData` is already
  // the current page (filtered by `search` and ordered by `sortBy`/`sortDir`,
  // defaulting to newest-first).
  const totalItems = usersResponse?.meta?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalItems / (itemsPerPage ?? 10)));

  const handlePageChange = (page: number) => { setPageState((prev) => ({ ...prev, currentPage: page })); };
  const handleItemsPerPageChange = (value: string) => { setPageState((prev) => ({ ...prev, itemsPerPage: Number(value), currentPage: 1 })); };

  // If a search narrows results so the persisted page no longer exists, snap back.
  useEffect(() => {
    if (usersResponse && (currentPage ?? 1) > totalPages) {
      setPageState((prev) => ({ ...prev, currentPage: 1 }));
    }
  }, [usersResponse, currentPage, totalPages, setPageState]);

  const pagination = { totalPages, currentPage, itemsPerPage, totalItems, handlePageChange, handleItemsPerPageChange };

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <h2 className="text-xl font-bold text-red-600 mb-4">Error Loading Users</h2>
          <p className="text-[var(--secondary-text-color)] mb-4">Failed to load users</p>
          <Button onClick={() => mutate()} className="bg-[var(--primary-color)] text-white">Retry</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto">
      <div className="page-header flex justify-between items-center h-[48px] px-[16px] border-b border-[var(--layout-border-color)]">
        <p className="page-heading text-[20px] leading-[30px] font-semibold">
          Users
          {usersResponse?.meta?.total !== undefined && (
            <span className="text-[13px] font-normal text-[var(--secondary-text-color)] ml-2">
              ({usersResponse.meta.total} total)
            </span>
          )}
        </p>
        <div className="search-bar-n-btns flex justify-between gap-[4px] items-center">
          <div className="search-bar relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#676879] w-4 h-4" />
            <input
              className="search h-[32px] pr-[12px] pl-[34px] text-[13px] leading-[20px] font-normal rounded-[4px] border border-[#C3C6D4] placeholder:text-[13px] placeholder:leading-[20px] placeholder:font-normal placeholder:text-[#676879] flex items-center"
              type="text"
              placeholder="Search by email, name, or wallet"
              value={searchQuery ?? ""}
              onChange={(e) => setPageState((prev) => ({ ...prev, searchQuery: e.target.value, currentPage: 1 }))}
            />
          </div>
        </div>
      </div>

      <div className="p-[16px]">
        <div className="pt-3 h-[calc(100vh-130px)]">
          {!usersData.length && isLoading ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="h-6 w-6 animate-spin text-[var(--secondary-text-color)]" />
            </div>
          ) : !usersData.length ? (
            <NoRecordFound title="Users" description="There are no users matching your search." />
          ) : (
            <CommonGrid
              rowData={usersData}
              isLoading={isLoading}
              columns={tableColumns}
              pagination={pagination}
              isPaginationRequired={true}
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              onSort={handleSortChange}
            />
          )}
        </div>
      </div>

      <ConfirmModal
        open={!!verifyingUser}
        onClose={() => setVerifyingUser(null)}
        onConfirm={handleVerifyUser}
        title="Verify User"
        description={`Are you sure you want to manually verify ${verifyingUser?.email}? This will mark their email as verified.`}
        confirmText="Verify"
        cancelText="Cancel"
      />

      <ConfirmModal
        open={!!mfaResetUser}
        onClose={() => setMfaResetUser(null)}
        onConfirm={handleResetMfa}
        title="Reset two-factor authentication?"
        description={`This will remove every second factor enrolled by ${mfaResetUser?.email} so they can sign in with email and password again. Only run this after verifying their identity out-of-band.`}
        confirmText="Reset 2FA"
        cancelText="Cancel"
        variant="delete"
      />
    </div>
  );
}
